import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const GovernmentContracting = ({ onGetStarted }) => {
  const benefits = [
    'Federal acquisition regulations (FAR) compliance',
    'Proposal preparation and bid support',
    'Contract negotiation and administration',
    'Small business certification assistance',
    'Compliance audits and investigations',
    'Dispute resolution and claims',
  ];

  const process = [
    { title: 'Registration & Certification', description: 'Obtain required registrations and small business certifications.' },
    { title: 'Bid Preparation', description: 'Develop compliant proposals and respond to solicitations.' },
    { title: 'Contract Negotiation', description: 'Negotiate terms and ensure FAR compliance.' },
    { title: 'Performance & Compliance', description: 'Ongoing compliance support and dispute resolution.' },
  ];

  const contractingAreas = [
    'FAR Compliance',
    'GSA Schedule Contracts',
    'IDIQ Contracts',
    'Small Business Set-Asides',
    'Teaming Agreements',
    'Subcontracting',
    'Cost Accounting Standards',
    'Contract Modifications',
  ];

  const whyChoose = [
    { icon: Award, title: 'Government Expertise', description: 'Deep knowledge of federal procurement regulations.' },
    { icon: Shield, title: 'Compliance Assurance', description: 'Ensure adherence to complex government requirements.' },
    { icon: Clock, title: 'Timely Support', description: 'Meet critical proposal and compliance deadlines.' },
    { icon: TrendingUp, title: 'Win Strategy', description: 'Strategic guidance to win government contracts.' },
  ];

  const faqs = [
    { question: 'What is required to contract with the federal government?', answer: 'You must register in SAM.gov (System for Award Management), obtain a DUNS number, and maintain active registration. Depending on contract type, you may need small business certifications, cybersecurity compliance (CMMC), and specific insurance coverage.' },
    { question: 'What are small business set-aside contracts?', answer: 'Set-asides are contracts reserved for small businesses including 8(a), HUBZone, woman-owned, veteran-owned, and service-disabled veteran-owned businesses. These programs provide opportunities to compete for contracts with less competition from large businesses.' },
    { question: 'What is the Federal Acquisition Regulation (FAR)?', answer: 'The FAR is the primary regulation governing federal procurement. It establishes uniform policies and procedures for government acquisitions. Contractors must comply with FAR requirements regarding pricing, compliance, labor standards, and reporting.' },
    { question: 'How long do government contracts take to award?', answer: 'Timeline varies significantly based on contract type and complexity. Simple purchases may take weeks, while major procurements can take months to over a year. The process includes solicitation, proposal submission, evaluation, negotiations, and award.' },
    { question: 'What happens if there is a contract dispute?', answer: 'Government contract disputes can be resolved through various mechanisms including Contracting Officer decisions, Board of Contract Appeals, Court of Federal Claims, or Alternative Dispute Resolution. We represent contractors in all dispute resolution forums.' },
  ];

  return (
    <div className="pt-20">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1594581979864-36977b15d0dc?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Compliance & Protection Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Government Contracting</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Navigate federal contracting requirements and compliance. Expert guidance from proposal through contract performance.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Government contracting involves complex regulations and compliance requirements. Our attorneys guide contractors through registration, proposal preparation, contract negotiation, and ongoing compliance with federal acquisition regulations.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We help businesses successfully navigate the federal marketplace.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1703107819041-5c1d6c35c085?w=800&q=80" alt="Government contracting services" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Government Contracting Services</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">Comprehensive support for federal contractors.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {contractingAreas.map((area, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{area}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">Strategic support throughout the government contracting lifecycle.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1618656172765-26774a4a38d2?w=800&q=80" alt="Why choose our government contracting team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8112180/pexels-photo-8112180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Ready to Win Government Contracts?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for expert government contracting legal services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default GovernmentContracting;
